import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import AIPanel from "@/components/AIPanel";

function md(text) {
  const parts = text.split(/```/);
  return parts.map((part, i) => {
    if (i % 2 === 1) return <pre key={i}><code>{part.replace(/^\w*\n/, "").replace(/\n$/, "")}</code></pre>;
    const html = part
      .replace(/^### (.*)$/gm, "<h3>$1</h3>").replace(/^## (.*)$/gm, "<h2>$1</h2>")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>").replace(/\*([^*]+)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/^> (.*)$/gm, "<blockquote>$1</blockquote>")
      .replace(/^- (.*)$/gm, "<li>$1</li>")
      .replace(/(<li>.*<\/li>)/s, "<ul>$1</ul>")
      .replace(/\n{2,}/g, "</p><p>").replace(/\n/g, "<br/>");
    return <div key={i} className="md" dangerouslySetInnerHTML={{ __html: `<p>${html}</p>` }} />;
  });
}

export default function TaskPage() {
  const { id, day } = useParams();
  const nav = useNavigate();
  const [task, setTask] = useState(null);
  const [page, setPage] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMin, setAiMin] = useState(false);
  const [reflectOpen, setReflectOpen] = useState(false);
  const [reflection, setReflection] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {passed, score, message, suggestions}

  const load = async () => {
    try { const { data } = await api.get(`/courses/${id}/tasks/${day}`); setTask(data); setPage(0); }
    catch (e) { toast.error(apiErr(e)); nav(`/courses/${id}`); }
  };
  useEffect(() => { load(); }, [id, day]);

  if (!task) return <div className="min-h-screen flex items-center justify-center text-teal-accent">Loading…</div>;
  const isLast = page === task.pages.length - 1;

  const submitReflection = async () => {
    if (reflection.trim().length < 500) return toast.error(`Please write at least 500 characters (${reflection.trim().length}/500).`);
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${id}/tasks/${day}/complete`, { reflection });
      if (data.passed) {
        toast.success(data.course_completed ? "🎉 Course complete! Jobs unlocked." : `Scored ${data.score}/10 — passed!`);
        setResult({ passed: true, score: data.score, message: data.message, courseComplete: data.course_completed });
        setReflection("");
        load();
      } else {
        setResult({ passed: false, score: data.score, message: data.message, suggestions: data.suggestions });
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const block = (e) => { e.preventDefault(); toast.error("Copy-paste is disabled. Please type your reflection."); };

  return (
    <div className="min-h-screen flex">
      <div className={`flex-1 transition-all ${aiOpen && !aiMin ? "lg:mr-[400px]" : ""}`}>
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => nav(`/courses/${id}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-teal-accent"><ArrowLeft size={16} /> {task.course_title}</button>
            <button data-testid="task-ai-btn" onClick={() => { setAiOpen(true); setAiMin(false); }}
                    className="kiq-btn px-4 py-2 text-sm flex items-center gap-2"><Sparkles size={15} /> AI</button>
          </div>

          <div className="glass-strong rounded-3xl p-8" data-testid="task-content">
            <div className="text-xs text-teal-accent uppercase tracking-widest mb-2">Day {task.day} of {task.total_days}</div>
            <h1 className="serif text-3xl sm:text-4xl font-extrabold mb-2">{task.heading}</h1>
            <div className="flex items-center gap-2 mb-6">
              {task.pages.map((_, i) => (
                <button key={i} onClick={() => setPage(i)} className={`h-1.5 rounded-full transition-all ${i === page ? "w-8 bg-[#00D4D4]" : "w-4 bg-white/15"}`} />
              ))}
              <span className="text-xs text-muted-foreground ml-2">{task.pages[page].title}</span>
            </div>
            <div className="leading-relaxed">{md(task.pages[page].content)}</div>
          </div>

          <div className="flex items-center justify-between mt-6">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}
                    className="glass rounded-full px-5 py-2.5 text-sm disabled:opacity-40 flex items-center gap-2"><ArrowLeft size={15} /> Previous</button>
            {isLast ? (
              <button data-testid="complete-task-btn" onClick={() => setReflectOpen(true)} disabled={task.completed}
                      className="kiq-btn px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-70">
                {task.completed ? <><Check size={16} /> You have already completed this task</> : <>Complete Task <Check size={16} /></>}
              </button>
            ) : (
              <button data-testid="next-page-btn" onClick={() => setPage((p) => p + 1)} className="kiq-btn px-5 py-2.5 text-sm flex items-center gap-2">Next <ArrowRight size={15} /></button>
            )}
          </div>
        </div>
      </div>

      {/* AI drawer */}
      {aiOpen && !aiMin && (
        <div className="fixed top-0 right-0 h-screen w-full sm:w-[400px] z-40 border-l border-border/60">
          <AIPanel mode="course" sessionId={`course-${id}`}
                   context={`Course: ${task.course_title}. Day ${task.day}: ${task.heading}.`}
                   onMinimize={() => setAiMin(true)} onClose={() => setAiOpen(false)} />
        </div>
      )}
      {aiOpen && aiMin && (
        <button data-testid="ai-restore" onClick={() => setAiMin(false)}
                className="fixed bottom-6 right-6 z-40 kiq-btn w-14 h-14 rounded-full flex items-center justify-center shadow-lg"><Sparkles size={22} /></button>
      )}

      {/* Reflection modal */}
      {reflectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => { setReflectOpen(false); setResult(null); }}>
          <div className="glass-strong rounded-3xl p-7 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="reflection-modal">
            {result && result.passed ? (
              <div className="text-center py-4" data-testid="reflection-passed">
                <div className="serif text-4xl font-bold text-teal-accent mb-2">{result.score}/10</div>
                <p className="text-sm mb-6">{result.message}</p>
                <button data-testid="reflection-continue" onClick={() => { setReflectOpen(false); setResult(null); }}
                        className="kiq-btn w-full py-3 text-sm">Continue</button>
              </div>
            ) : (
              <>
                <h3 className="serif text-2xl font-bold mb-1">What did you learn?</h3>
                <p className="text-sm text-muted-foreground mb-4">Type at least 500 characters describing what you learned. Copy-paste is disabled.</p>
                {result && !result.passed && (
                  <div className="glass rounded-xl p-4 mb-4 border border-red-400/30" data-testid="reflection-failed">
                    <div className="text-red-400 font-semibold text-sm mb-1">Scored {result.score}/10 — {result.message}</div>
                    {result.suggestions && <div className="text-xs text-muted-foreground leading-relaxed mt-2"><span className="text-teal-accent">How to improve:</span> {result.suggestions}</div>}
                  </div>
                )}
                <textarea data-testid="reflection-input" value={reflection}
                          onChange={(e) => setReflection(e.target.value)}
                          onPaste={block} onCopy={block} onCut={block} onContextMenu={block}
                          rows={10} placeholder="Today I learned…"
                          className="kiq-input w-full px-4 py-3 text-sm resize-none" autoFocus />
                <div className="flex items-center justify-between mt-2 text-xs">
                  <span className={reflection.trim().length >= 500 ? "text-teal-accent" : "text-muted-foreground"}>
                    {reflection.trim().length}/500 minimum
                  </span>
                </div>
                <button data-testid="submit-reflection" disabled={busy || reflection.trim().length < 500} onClick={submitReflection}
                        className="kiq-btn w-full py-3 mt-4 text-sm disabled:opacity-50">{busy ? "Evaluating…" : "Submit & complete"}</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
